import { Observable, throwError, of } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { Injectable } from '@angular/core';
import { HelpConfig } from '../core/config/help-config';
import { LogoutType } from '../core/enums/logout-types.enum';
import { Token } from '../core/outros/token.model';
import { AuthService } from '../core/service/auth.service';
import { TokenService } from '../core/services/outros/token.service';
import { UserControllerService } from '../core/services/outros/user-controller.service';

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {

  private refreshInProgress$: Observable<Token> | null = null;

  constructor(
    private userControllerService: UserControllerService,
    private helpConfig: HelpConfig,
    private authService: AuthService,
    private tokenService: TokenService
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authToken = this.userControllerService.recoverFromStorage();
    const shouldAttachToken =
      !!authToken?.access_token &&
      this.isApiRequest(req.url) &&
      !req.params.has('noToken');

    if (!shouldAttachToken) {
      return next.handle(req);
    }

    const modifiedReq = this.addTokenHeader(req, authToken.access_token);

    return next.handle(modifiedReq).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !this.isAuthSessionRequest(req.url)
        ) {
          return this.handle401Error(req, next);
        }

        return throwError(() => error);
      })
    );
  }

  /** Só anexa Bearer em requisições ao host/base da API (RINSPECTION_WEB). */
  private isApiRequest(url: string): boolean {
    const baseApi = this.helpConfig.BASE_API;
    if (!baseApi || baseApi === '/') {
      return false;
    }

    if (url.startsWith(baseApi)) {
      return true;
    }

    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return new URL(url).origin === new URL(baseApi).origin;
      }
    } catch {
      return false;
    }

    return false;
  }

  private isAuthSessionRequest(url: string): boolean {
    const normalized = url.toLowerCase();
    return (
      normalized.includes('auth/refreshtoken') ||
      normalized.includes('auth/signin') ||
      normalized.endsWith('/auth') ||
      normalized.includes('/auth?')
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const current = this.tokenService.getToken();
    if (!current?.refresh_token) {
      this.sessionExpiredLogout();
      return throwError(() => new Error('Refresh token unavailable'));
    }

    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.authService.refreshToken().pipe(
        switchMap((newToken: Token) => {
          if (!newToken?.access_token) {
            return throwError(() => new Error('Refresh token failed'));
          }
          this.tokenService.setToken(newToken);
          return of(newToken);
        }),
        catchError((err) => {
          this.sessionExpiredLogout();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInProgress$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.refreshInProgress$.pipe(
      switchMap((token) => next.handle(this.addTokenHeader(request, token.access_token)))
    );
  }

  private addTokenHeader(request: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private sessionExpiredLogout(): void {
    this.userControllerService.logout(LogoutType.Expires);
  }
}
